import React, { useState, useEffect, useRef, forwardRef } from 'react';
import { Box, Button, Collapse, Icon, List, ListItem, ListItemText, Checkbox, ListItemSecondaryAction, IconButton, TextField, LinearProgress, FormLabel, FormControl, Typography } from '@mui/material';
import { get, last, set, size, unset, noop, uniqBy } from 'global/lib/lodash';
import clsx from 'clsx';
import arrayToTree from 'array-to-tree';
import Modal from 'global/components/modal';
import { DivisionIcon, CommunityIcon } from 'global/components/PerformIcons';
import { _size } from 'global/utils';
import sendData from 'redux/actions/sendData';
import SnackAlerts from 'global/components/snack-alerts';
import ConfirmModal from 'global/components/confirm';
import { flattenAllRealms, sortAlphaNum, uppercaseFirst } from '../../utils';
import useRetrieveData from 'global/hooks/useRetrieveData';
import LotIcon from '@mui/icons-material/Home';
import CompanyIcon from '@mui/icons-material/Business';
import LocationIcon from '@mui/icons-material/AddLocation';
import { useScreen } from '../../styles';
import useStyles from 'global/styles/filters';
import { Loader } from 'global/styles';
import dropStyles from './styles';
import { useUser } from '../../hooks/useUser';
import { buildFilters } from '../../utils/filter';
import { Column } from 'global/components/row';
import { keyBy } from '../../lib/lodash';
import { Row } from '../row';
import { Text } from '../text';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';

export const Dropitems = (props) => {
  const { items, title, type, icon, showModify, alwaysShow } = props;
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState({});
  const [accessOpen, setAccessOpen] = useState(false);
  const classes = dropStyles();

  useEffect(() => {
    const map = {};
    (items || []).map((r) => set(map, r.id, r));
    setSel(map);
  }, []);

  // Re-sync sel from props when modal opens so clicking "Update" without
  // changes doesn't send an empty selection back to the parent.
  useEffect(() => {
    if (accessOpen) {
      if (props.selected && typeof props.selected === 'object') {
        setSel({ ...props.selected });
      } else {
        const map = {};
        (items || []).forEach((r) => set(map, r.id, r));
        setSel(map);
      }
    }
  }, [accessOpen]);

  const onUpdate = async () => {
    props.onUpdate && (await props.onUpdate(sel));
    setAccessOpen(false);
  };

  const onSelectedChange = (s) => {
    setSel(s);
  };

  const renderTitle = () => {
    if (title) {
      return title;
    }

    const pluralized = `${type}${size(items) !== 1 ? 's' : ''}`;

    return (
      <>
        <span style={{ marginLeft: 5 }}>{`${size(items)} ${pluralized}`}</span>
      </>
    );
  };

  const hasitems = size(items) > 0;

  const modalProps = {
    ...(props.modalProps
      ? {
          open: accessOpen,
          buttons: [{ title: 'Update', onClick: onUpdate }],
          ...props.modalProps,
          onClose: async () => {
            props.modalProps.onClose && (await props.modalProps.onClose());
            setAccessOpen(false);
          },
        }
      : {
          open: accessOpen,
          title: 'SET ACCESS',
          content: <Access {...props} onSelectedChange={onSelectedChange} />,
          onClose: () => setAccessOpen(false),
          buttons: [{ title: 'Update', onClick: onUpdate }],
        }),
  };

  if (props.render) {
    return (
      <>
        {props.render({ openModal: setAccessOpen, selected: sel, isOpen: accessOpen })}
        <Modal {...modalProps} />
      </>
    );
  }

  return !hasitems && !showModify ? null : (
    <>
      <div className={classes.titleRow}>
        {!!icon && <div className={classes.realmRowIcon}>{icon}</div>}
        <div className={classes.title}>{renderTitle()}</div>

        {hasitems && !alwaysShow && (
          <Button startIcon={<Icon>{open ? 'arrow_drop_up' : 'arrow_drop_down'}</Icon>} onClick={() => setOpen(!open)} className={classes.toggleButton}>
            {open ? 'HIDE' : 'SHOW'}
          </Button>
        )}

        {showModify && (
          <Button onClick={() => setAccessOpen(!accessOpen)} className={classes.toggleButton}>
            {'MODIFY'}
          </Button>
        )}
      </div>

      <Collapse in={open || alwaysShow}>
        <div className={classes.marginLeft}>
          {items.map((realm, i) => {
            if (props.renderItem) {
              return props.renderItem(realm, i, classes);
            }

            return (
              <div key={`dropRealmsItems-${realm.id ?? i}`} className={clsx(classes.titleRow, classes.title)}>
                {icon}
                <div className={classes.realmText}>{get(realm, 'name', '')}</div>
              </div>
            );
          })}
        </div>
      </Collapse>

      <Modal {...modalProps} />
    </>
  );
};

export const buildRealmTree = (realms) => {
  return arrayToTree(realms, { parentProperty: 'parentId' });
};

export const getRealmIcon = (realm, iconClass) => {
  if (!realm) {
    return null;
  }
  const tier = get(realm, 'tier', false);
  if (get(realm, 'id') === -1) {
    return <Icon className={iconClass}>add_box</Icon>;
  }
  return tier === 'division' ? (
    <DivisionIcon className={iconClass} />
  ) : tier === 'community' ? (
    <CommunityIcon className={iconClass} />
  ) : tier === 'lot' ? (
    <LotIcon data-testid="lotIcon" className={iconClass} />
  ) : tier === 'builder' ? (
    <CompanyIcon data-testid="builderIcon" className={iconClass} />
  ) : tier === 'shared' ? (
    <SupervisorAccountIcon data-testid="sharedIcon" className={iconClass} />
  ) : (
    <LocationIcon data-testid="locationIcon" className={iconClass} />
  );
};

export const sortRealmsAlphabetically = (realms) => {
  return realms.sort((a, b) => sortAlphaNum(a, b, 'name'));
};

export let ALLREALMS = {};

export const Access = forwardRef((props, ref) => {
  const { realms, allRealms, editable, singleSelect, canAdd } = props;
  const { me, userBuilderRealm } = useUser();
  let [selected, setSelected] = useState({});
  let [currentRealm, setCurrentRealm] = useState(props.currentRealm || []);
  let [hist, setHist] = useState([]);
  let [topTree, setTopTree] = useState([]);
  const [currentEdit, setCurrentEdit] = useState();
  const [loading, setLoading] = useState();
  const [alerts, setAlerts] = useState();
  const [confirmModal, setConfirmModal] = useState();
  const [modal, setModal] = useState();
  const [allFlat, setAllFlat] = useState([]);
  const { isMobileXS } = useScreen();
  const hasUpdated = useRef(false);
  const isLotLoad = useRef(false);

  const singleTierSelect = get(props, 'singleTierSelect', true);

  const open = get(props, 'open', true);
  const meRealm = get(me, 'realm.id');

  const showKey = get(props, 'showKey', false);
  const useCurrentChange = get(props, 'useCurrentChange', true);
  const dontSelectCurrent = get(props, 'dontSelectCurrent', false);

  const realmData = useRetrieveData(
    { url: `/realms?realmPicker=1&clientKey=${showKey ? 1 : 0}` },
    {
      logoutOnUnauth: true,
      build: ['realms'],
      waitFor: allRealms ? null : undefined,
      transformData: (d) => {
        ALLREALMS = keyBy(d, 'id');
        return d;
      },
    }
  );

  const useRealmData = !allRealms;
  const _loading = loading || (useRealmData && realmData.loading);
  const classes = dropStyles({ isMobile: isMobileXS, loading: _loading });

  const findCurrentRealmInTree = (tree, toFind) => {
    let found;
    let foundArr = [];
    let parent;
    let parentArr = [];

    const search = (arr = [], parents = []) => {
      found = (arr || []).find((r) => r.id == toFind.id);
      parent = parent || (arr || []).find((r) => r.id == toFind.parentId);
      if (parent && !size(parentArr)) {
        parentArr = parents;
      }
      if (!found) {
        arr.find((ar) => {
          found = search(ar.children, [...parents, ar]);
          return found;
        });
      } else {
        foundArr = parents;
      }
      return found;
    };
    search(tree);

    return { found, foundArr, parent, parentArr };
  };

  const runInitial = (bypassCurrentRealm = false) => {
    setLoading(true);

    if (useRealmData && realmData.loading) {
      return;
    }

    const aRealms = sortRealmsAlphabetically(allRealms || flattenAllRealms(realmData.data));
    let tree = buildRealmTree(aRealms);
    if (props.showOnlyUsersBuilder) {
      tree = tree.filter((t) => {
        if (t.id == userBuilderRealm.id) {
          return true;
        }
        return false;
      });
    }
    let topLevel = { children: !!size(topTree) ? topTree : tree };
    let newHist = [topLevel];
    let company = tree.find((t) => t.id === meRealm) || get(tree, '0');
    let setRealm = false;

    const sel = editable ? { ...selected } : props.selected || {};
    (realms || []).map((r) => set(sel, r.id, r));

    const crealm = get(currentRealm, 'children') ? currentRealm : props.currentRealm;

    if (!bypassCurrentRealm && crealm) {
      const { found, foundArr, parent, parentArr } = findCurrentRealmInTree(tree, crealm);
      if (found) {
        if (found.tier === 'builder' || size(foundArr)) {
          const hasUp = get(hasUpdated, 'current');
          newHist = [...newHist, ...foundArr];
          company = found;

          if (!hasUp && (size(foundArr) > 1 || props.openToSelected) && !props.openToChildren) {
            if (size(get(last(newHist), 'children'))) {
              company = newHist.pop();
            }
          }
        }
        if (!editable && !(props.canOnlySelectUsersBuilder && found.tier === 'builder' && found.id !== userBuilderRealm.id)) {
          if (props.onlyTiers) {
            if (props.onlyTiers.indexOf(found.tier) === -1) {
              (foundArr || []).map((fa) => newHist.push(fa));
              setRealm = found;
              newHist = uniqBy(newHist, 'id');
            } else {
              set(sel, found.id, found);
            }
          } else {
            set(sel, found.id, found);
          }
        }
      } else if (parent) {
        company = parent;
        newHist = [...newHist, ...parentArr];
        isLotLoad.current = true;
      }
    }

    const compCheck = () => {
      if (size(get(company, 'children')) === 1) {
        const compIsTheFirstChildSelected = get(sel, get(company, 'children.0.id'));
        if (compIsTheFirstChildSelected) {
          return;
        }

        newHist.push(company);
        company = company.children[0];
        compCheck();
      } else if (size(get(company, 'children')) === 0) {
        company = newHist.pop();
      }
    };

    const hasNewHist = size(get(last(newHist), 'children')) > 0;
    const hasCurrentRealm = !bypassCurrentRealm && !!crealm && size(crealm);
    if (useRealmData || props.useNewHist) {
      if (!hasCurrentRealm && hasNewHist && size(tree) > 1) {
        company = newHist.pop();
      } else if (size(get(company, 'children')) === 1) {
        compCheck();
      }
    }

    if (get(company, 'id') == get(currentRealm, 'id') && !!company) {
      company = {
        ...currentRealm,
        ...company,
        loaded: false,
      };
    }

    if (dontSelectCurrent && crealm && crealm.tier == 'builder') {
      unset(sel, get(crealm, 'id'));
    }

    setAllFlat(aRealms);
    setTopTree(tree);
    selected = sel;
    setSelected(sel);
    setHist(newHist);
    setCurrentRealm(open ? (setRealm ? setRealm : company) : undefined);
    setLoading(false);
  };

  useEffect(() => {
    if (!!open) {
      runInitial();
    }
  }, [allRealms, realmData.data, realmData.loading, open]);

  const reset = () => {
    hist = [];
    topTree = [];
    currentRealm = [];
    selected = {};
    runInitial(true);
    setCurrentEdit();
  };

  set(ref, 'current.reset', reset);

  useEffect(() => {
    window.MASTERREALM = realmData;

    return () => {
      unset(window, 'MASTERREALM');
    };
  }, []);

  useEffect(() => {
    set(hasUpdated, 'current', false);
    if (!open && useRealmData) {
      setCurrentRealm();
    }
  }, [open]);

  useEffect(() => {
    if (!useCurrentChange) {
      return;
    }

    if (props.currentRealm && !(props.onlyTiers && props.onlyTiers.indexOf(props.currentRealm.tier) === -1)) {
      setSelected({
        [get(props, 'currentRealm.id')]: get(props, 'currentRealm'),
      });
    } else if (singleSelect) {
      setSelected({});
    } else {
      setSelected({ ...selected });
    }
  }, [props.currentRealm]);

  useEffect(() => {
    if (currentRealm) {
      props.onCurrentRealm && props.onCurrentRealm(!currentRealm.id ? null : currentRealm);
    }
  }, [currentRealm]);

  const getLots = async () => {
    try {
      const { id } = currentRealm;
      setLoading(true);
      const children = await sendData(
        { url: `/realms` },
        {
          params: {
            ...buildFilters({
              parent_id: id,
            }),
            clientKey: showKey ? 1 : undefined,
          },
          normalize: {
            build: ['realms'],
          },
        }
      );
      setCurrentRealm({
        ...currentRealm,
        loaded: true,
        children: sortRealmsAlphabetically(children || []),
      });

      if (isLotLoad.current && props.currentRealm && props.openToSelected) {
        //because lots aren't findable, we are setting selected in this special circumstance
        const sel = { ...selected };
        set(sel, get(props, 'currentRealm.id'), props.currentRealm);
        setSelected(sel);
        isLotLoad.current = false;
      }
    } catch (err) {
      setAlerts({ message: err.message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open || loading || (realmData.loading && useRealmData)) {
      return;
    }
    const tier = get(currentRealm, 'tier');
    const loaded = get(currentRealm, 'loaded', false);
    if (tier === 'community' && !loaded) {
      getLots();
    }
  }, [currentRealm, open, loading]);

  const stopProp = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const back = () => {
    const _hist = [...hist];
    const prevRealm = _hist.pop();
    setHist(_hist);
    setCurrentRealm(size(_hist) === 0 ? { children: topTree } : prevRealm);
    checkDeselect();
    setCurrentEdit();
  };

  const checkDeselect = () => {
    if (props.deselectOnChange) {
      setSelected({});
    }
  };

  const selectChildren = (e, realm) => {
    if (realm.tier === 'lot') {
      return;
    }
    stopProp(e);
    const _hist = [...hist];
    _hist.push(currentRealm);
    setHist(_hist);
    setCurrentRealm(realm);
    checkDeselect();
    setCurrentEdit();
  };

  const editClick = (e, realm) => {
    stopProp(e);
    setCurrentEdit({
      ...realm,
      value: get(realm, 'name', ''),
    });
  };

  const findParent = (realm) => {
    return allFlat.find((f) => f.id == realm.parentId);
  };

  const findMasterParent = (realm) => {
    let topRealm;
    for (let i = 0; i < hist.length; i++) {
      const item = hist[i];
      if (item.id) {
        topRealm = {
          ...item,
          children: undefined,
        };
        break;
      }
    }
    if (!topRealm) {
      topRealm = {
        ...currentRealm,
        children: undefined,
      };
    }
    return topRealm;
  };

  const findTopDivision = (realm) => {
    let topRealm;
    if (realm.tier === 'division') {
      return realm;
    }

    for (let i = 0; i < hist.length; i++) {
      const item = hist[i];
      if (item.id && item.tier === 'division') {
        topRealm = {
          ...item,
          children: undefined,
        };
        break;
      }
    }
    if (!topRealm && currentRealm.tier === 'division') {
      topRealm = {
        ...currentRealm,
        children: undefined,
      };
    }

    return topRealm;
  };

  const onlySelectSameTier = get(props, 'onlySelectSameTier', true);
  const itemSelect = (realm, e) => {
    stopProp(e);
    realm = {
      ...realm,
      topRealm: findMasterParent(realm),
      topDivision: findTopDivision(realm),
    };
    let sel = { ...selected };
    const isSelected = get(sel, realm.id);
    if (props.onlyTiers) {
      if (props.onlyTiers.indexOf(realm.tier) === -1) {
        const isTemplate = size(props?.onlyTiers) === 3 && props.onlyTiers.indexOf('builder') > -1;
        alert(`Locations of type "${realm.tier.charAt(0).toUpperCase() + realm.tier.slice(1)}" are not selectable for ${isTemplate ? 'template creation' : 'this action'}.`);
        return;
      }
    }
    if (singleSelect) {
      sel = { [realm.id]: realm };
    } else {
      if (isSelected) {
        delete sel[realm.id];
      } else {
        set(sel, realm.id, realm);
        if (onlySelectSameTier) {
          Object.keys(sel).map((k) => {
            const r = sel[k];
            if (r.tier != realm.tier) {
              delete sel[k];
            }
          });
        }
      }
    }

    if (singleTierSelect && !singleSelect) {
      for (let k in sel) {
        const obj = sel[k];
        if (obj.tier !== realm.tier) {
          unset(sel, k);
        }
      }
    }

    setSelected(sel);
    props.onSelectedChange && props.onSelectedChange(sel);
  };

  const currentTierBasedOnCurrentRealm = () => {
    const parentTier = get(currentRealm, 'tier');
    if (get(currentRealm, 'children.0.tier')) {
      return get(currentRealm, 'children.0.tier');
    } else {
      if (parentTier === 'division') {
        return 'community';
      } else if (parentTier === 'community') {
        return 'lot';
      }
    }
  };

  const saveEdit = async () => {
    try {
      const realm = currentEdit;

      if (!realm.value) {
        return alert('Name cannot be blank.');
      }

      setLoading(true);

      let resp;
      if (realm.id === -1) {
        const tier = currentTierBasedOnCurrentRealm();
        resp = await sendData(
          {
            url: `/realms?clientKey=${showKey ? 1 : 0}`,
            method: 'POST',
            data: {
              data: {
                type: 'realms',
                attributes: {
                  user_id: tier !== 'room' ? undefined : me.id,
                  name: realm.value,
                  client_key: realm.clientKey,
                  tier,
                  parent_id: get(currentRealm, 'id'),
                },
              },
            },
          },
          { normalize: { build: ['realms'] } }
        );
      } else {
        resp = await sendData(
          {
            url: `/realms/${realm.id}?clientKey=${showKey ? 1 : 0}`,
            method: 'PATCH',
            data: {
              data: {
                id: realm.id,
                type: 'realms',
                attributes: {
                  name: realm.value,
                  client_key: realm.clientKey,
                },
              },
            },
          },
          { normalize: { build: ['realms'] } }
        );
      }

      const updateMethod = get(props, 'onEditSuccess', () => 0);

      await Promise.all([realmData.update(), updateMethod(resp)]);

      set(hasUpdated, 'current', true);
      setCurrentEdit();
    } catch (err) {
      const msg = err.message.indexOf('already exists') > -1 ? 'There is already an existing location with that name' : err.message;
      setAlerts({
        message: `Error: ${msg}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const updateNameNum = (arr) => {
    let val = '';
    const reN = /[0-9]+/g;
    const reA = /[^a-zA-Z]/g;
    let prevNums = [0];
    // start with the last item in the array as the baseline against which to compare
    const startName = get(arr.at(-1), 'name', '');
    if (Array.isArray(startName.match(reN))) {
      const startNum = startName.match(reN).at(-1);
      const startNumInt = parseInt(startNum, 10);
      const startNameNoNumbers = startName.replace(reA, '').toLowerCase();
      arr.reverse().forEach((r, i) => {
        if (i === 0) {
          val = startName.replace(startNum, (startNumInt + 1).toString().padStart(startNum.length, '0'));
        } else {
          if (i === 1) {
            val = '';
          }
          const thisName = get(r, 'name', '');
          const thisNameNoNumbers = thisName.replace(reA, '').toLowerCase();
          if (startNameNoNumbers === thisNameNoNumbers) {
            // We will use the last number if there are multiple matches
            const thisNameNumbers = thisName.match(reN);
            if (Array.isArray(thisNameNumbers) && thisNameNumbers.at(-1)) {
              const thisNumInt = parseInt(thisNameNumbers.at(-1), 10);
              if (thisNumInt < startNumInt) {
                prevNums.push(thisNumInt);
              }
            }
          }
        }
      });
      if (prevNums.length > 1) {
        prevNums.sort((a, b) => (a > b ? 1 : a < b ? -1 : 0));
        let finalNumInt = startNumInt + 1;
        if (startNumInt - prevNums.at(-1) === prevNums.at(-1) - prevNums.at(-2)) {
          finalNumInt = startNumInt * 2 - prevNums.at(-1);
        }
        val = startName.replace(startNum, finalNumInt.toString().padStart(startNum.length, '0'));
      }
    }
    return val;
  };

  const addNew = () => {
    let val = '';
    const visibleRealmsByName = get(currentRealm, 'children', []);
    if (visibleRealmsByName.length === 1) {
      val = updateNameNum(visibleRealmsByName);
    } else if (visibleRealmsByName.length > 1) {
      // Check if the last visible realm has a sibling realm with matching name and lower number
      // and then default to a new incremented name based on the results
      val = updateNameNum([...visibleRealmsByName]);
      if (!val) {
        // If not, check if the last _created_ realm has one
        const visibleRealmsByCreated = [...visibleRealmsByName].sort((a, b) => get(a, 'id') - get(b, 'id'));
        val = updateNameNum(visibleRealmsByCreated);
      }
    }
    setCurrentEdit({
      id: -1,
      value: val,
    });
  };

  const startMove = () => {
    let selMove;
    const selRealm = get(selected, Object.keys(selected)[0]);
    const parent = findParent(selRealm);
    setModal({
      title: 'Choose Realm to move selected realms to',
      open: true,
      onClose: () => setModal(),
      content: () => <Access singleSelect allRealms={allRealms} onlyTiers={[get(parent, 'tier')]} currentRealm={findParent(findParent(selRealm))} onSelectedChange={(sel) => (selMove = sel)} />,
      buttons: [
        { title: 'Cancel', onClick: () => setModal() },
        {
          title: 'Okay',
          variant: 'contained',
          onClick: () => {
            if (!selMove) {
              alert('No realm was selected!');
            } else {
              archive('move', get(selMove, Object.keys(selMove)[0]));
            }
            setModal();
          },
        },
      ],
    });
  };

  const archive = (type = 'archive', moveTo) => {
    const names = Object.keys(selected)
      .map((k) => get(selected, [k, 'name']))
      .join(', ');
    const content = type === 'move' ? `${names} to ${moveTo.name}` : names;
    setConfirmModal({
      title: 'Confirm',
      content: `Are you sure you want to ${type} ${content}?`,
      onOk: async () => {
        try {
          setLoading(true);
          setConfirmModal();

          if (type === 'archive') {
            await Promise.all(
              Object.keys(selected).map(async (k) => {
                const realm = get(selected, k);
                await sendData({
                  url: `/realms/${realm.id}`,
                  method: 'patch',
                  data: {
                    data: {
                      id: realm.id,
                      type: 'realms',
                      attributes: {
                        active: 0,
                      },
                    },
                  },
                });
              })
            );
          } else if (type === 'merge') {
            const remainingRealm = Object.keys(selected)[0];
            const realms = [];
            Object.keys(selected).map(async (k) => {
              if (k !== remainingRealm) {
                realms.push(k);
              }
            });
            await sendData({
              url: `/realms/${remainingRealm}/merge`,
              method: 'patch',
              data: {
                data: {
                  id: remainingRealm,
                  type: 'realms',
                  attributes: {
                    realms,
                  },
                },
              },
            });
          } else if (type === 'move') {
            await Promise.all(
              Object.keys(selected).map(async (k) => {
                const realm = selected[k];
                await sendData({
                  url: `/realms/${realm.id}`,
                  method: 'patch',
                  data: {
                    data: {
                      id: realm.id,
                      type: 'realms',
                      attributes: {
                        parent_id: get(moveTo, 'id'),
                      },
                    },
                  },
                });
              })
            );
          }
          await realmData.update();
          await (props.onEditSuccess && props.onEditSuccess());
          setAlerts({
            message: type === 'move' ? 'Location(s) moved successfully' : type === 'merge' ? 'Locations merged successfully' : 'Operation completed successfully',
            severity: 'success',
          });
          setSelected({});
          setLoading(false);
          setConfirmModal(false);
        } catch (err) {
          setLoading(false);
          setAlerts({
            message: `Error: ${err.message}`,
            severity: 'error',
          });
        }
      },
      onCancel: () => setConfirmModal(),
    });
  };

  const renderEditRow = (realm) => {
    return (
      <div onClick={stopProp} className={classes.itemEditContainer}>
        <div className={classes.editRow}>
          {getRealmIcon(realm, classes.realmRowIcon)}
          <Column center={false} fullWidth>
            <TextField
              variant={'outlined'}
              size="small"
              fullWidth={true}
              disabled={loading}
              value={currentEdit.value}
              onChange={(e) => setCurrentEdit({ ...currentEdit, value: e.target.value })}
              autoFocus
            />
            <TextField
              style={{ marginTop: 8 }}
              variant={'outlined'}
              size="small"
              fullWidth={true}
              disabled={loading}
              value={currentEdit.clientKey}
              placeholder={'Key ID (optional)'}
              onChange={(e) => setCurrentEdit({ ...currentEdit, clientKey: e.target.value })}
            />
          </Column>
        </div>
        <div className={classes.editButtonRow}>
          <Button onClick={() => setCurrentEdit()} startIcon={<Icon>close</Icon>} color={'secondary'} disabled={loading} variant="contained" className={classes.editCancel}>
            CANCEL
          </Button>

          <Button onClick={() => saveEdit()} startIcon={loading ? <Loader size={16} /> : <Icon>label</Icon>} color={'secondary'} disabled={loading} variant="contained" className={classes.editButton}>
            SAVE
          </Button>
        </div>
      </div>
    );
  };

  const renderTitle = () => {
    const obj = {};
    Object.keys(selected).forEach((k) => {
      const r = selected[k];
      const tier = get(r, 'tier');
      set(obj, tier, [...get(obj, tier, []), r]);
    });

    if (props.showSelectedText && _size(selected)) {
      const firstKey = Object.keys(selected)[0];
      return <div className={classes.greenText}>{`${get(selected, [firstKey, 'name'])} is selected`}</div>;
    }

    return Object.keys(obj)
      .map((k) => {
        if (k === 'community') {
          return `${size(obj[k])} ${size(obj[k]) > 1 ? 'Communities' : 'Community'}`;
        } else {
          return `${size(obj[k])} ${uppercaseFirst(get(obj[k], [0, 'tier'])) + (size(obj[k]) > 1 ? 's' : '')}`;
        }
      })
      .join(', ');
  };

  const canAddInCurrentContext = () => {
    const currentTier = get(currentRealm, 'tier', 'lot');
    const allowedTiers = props.tiersCanAdd || ['division', 'community'];

    if (!allowedTiers.includes(currentTier)) {
      return false;
    }
    return true;
  };

  const getRealmName = (realm) => {
    const realmname = get(realm, 'name', '');
    if (realm.tier === 'builder' && get(realm, 'id') !== get(me, 'realm.id')) {
      return `${realmname} (client)`;
    }
    return realmname;
  };

  const getCurrentItemTitle = () => {
    const [f, ...rest] = hist;
    const toMap = [...rest, currentItem];
    return (
      <div data-testid="realmTitle" className={clsx(classes.titleRow, 'realmTitleRow')}>
        {toMap
          .filter((r) => !!r.name)
          .map((r, i) => (
            <div key={`dropRealmsTitle${i}`} className={clsx(classes.titleRow, 'realmTitle')}>
              {i !== 0 && <div className="spacer">-</div>}
              {getRealmIcon(r)}
              {`${get(r, 'name')}`}
            </div>
          ))}
      </div>
    );
  };

  const currentItem = size(hist) > 0 && currentRealm;

  const isNewEdit = currentEdit && currentEdit.id === -1;
  const selSize = _size(selected);
  const showCheck = selSize > 0 || !singleSelect;
  const showAddNew = (() => {
    if (editable || canAdd) {
      return canAddInCurrentContext();
    } else {
      return false;
    }
  })();

  const canShowArrow = (() => {
    if (get(currentItem, 'tier') !== 'lot' && get(props, 'onlyTiers', []).indexOf('lot') > -1) {
      return true;
    } else if (canAdd && currentItem && currentItem.tier !== 'community') {
      return true;
    }
    return false;
  })();

  const showBack = (() => {
    const canBackOutOfBuilder = get(props, 'canBackOutOfBuilder', true);
    if (size(hist) > 0) {
      if (currentItem && currentItem.tier === 'builder') {
        return canBackOutOfBuilder;
      } else {
        return true;
      }
    } else {
      return false;
    }
  })();

  const shouldShowMove = (() => {
    if (selSize < 1) {
      return false;
    }
    let hasDivOrBuilder;
    for (let k in selected) {
      const tier = get(selected, [k, 'tier']);
      const noTiers = ['builder', 'division'];
      if (noTiers.indexOf(tier) > -1) {
        hasDivOrBuilder = true;
        break;
      }
    }
    return !hasDivOrBuilder;
  })();

  const shouldShowArchive = (() => {
    if (selSize < 1) {
      return false;
    }
    let hasBuilder;
    for (let k in selected) {
      const tier = get(selected, [k, 'tier']);
      const noTiers = ['builder'];
      if (noTiers.indexOf(tier) > -1) {
        hasBuilder = true;
        break;
      }
    }
    return !hasBuilder;
  })();

  const renderSelectTitle = () => {
    const tier = get(currentRealm, 'tier');

    const tiers = {
      default: {
        current: <span>{getRealmIcon({ tier: 'builder' })}Builder</span>,
        child: <span>click the name to see {getRealmIcon({ tier: 'division' })}Divisions.</span>,
      },
      builder: {
        current: <span>{getRealmIcon({ tier: 'division' })}Division</span>,
        child: <span>click the name to see {getRealmIcon({ tier: 'community' })}Communities.</span>,
      },
      division: {
        current: <span>{getRealmIcon({ tier: 'community' })}Community</span>,
        child: <span>click the name to see {getRealmIcon({ tier: 'lot' })}Lots.</span>,
      },
      community: {
        current: <span>{getRealmIcon({ tier: 'lot' })}Lot</span>,
        child: <span>use the back button to see other locations</span>,
      },
    };

    const sel = get(tiers, tier, tiers.default);
    return (
      <div className={clsx(classes.titleRow, 'selectTitleRow')}>
        {props.selectTitle || null}
        <span>
          {`Select a`}
          {get(sel, 'current')}
          {` by clicking the ${showCheck ? 'checkbox' : 'plus'} or `}
          {get(sel, 'child')}
        </span>
      </div>
    );
  };

  const onlyTiers = props.onlyTiers || [];

  const renderRowIcon = (realm, i) => {
    const isSelected = get(selected, realm.id);
    const ico = singleSelect && !props.selectTitle ? getRealmIcon({ tier: 'none' }, classes.realmRowIcon) : <Icon className={classes.realmRowIcon}>{isSelected ? 'check' : 'add'}</Icon>;
    const isDisabled = !size(onlyTiers) ? false : !onlyTiers.includes(realm.tier);
    const notSelectable = props.canOnlySelectUsersBuilder && realm.tier === 'builder' && realm.id !== userBuilderRealm.id;

    return (
      <div
        className={clsx(classes.listRowIcon, isSelected && classes.listRowIconSelected, isDisabled && classes.listRowIconDisabled, notSelectable && classes.notSelectable)}
        onClick={(e) => (notSelectable ? noop : itemSelect(realm, e))}
      >
        {!singleSelect && (!editable || showCheck) ? <Checkbox checked={!!isSelected} /> : ico}
      </div>
    );
  };

  return (
    <div className={classes.listWrap}>
      {showCheck && editable && selSize > 0 && (
        <div className={classes.archiveRow}>
          <div className={classes.archiveText}>{`${selSize} item${selSize > 1 ? 's' : ''} selected`}</div>
          <div className={classes.archiveButtonRow}>
            {!loading && selSize >= 2 && (
              <Button size="small" disabled={loading} variant="contained" onClick={() => archive('merge')} color="secondary">
                MERGE
              </Button>
            )}
            {!loading && shouldShowMove && (
              <Button size="small" disabled={loading} variant="contained" onClick={startMove} color="secondary">
                MOVE
              </Button>
            )}
            {!loading && shouldShowArchive && (
              <Button size="small" disabled={loading} variant="contained" onClick={() => archive()} color="secondary">
                ARCHIVE
              </Button>
            )}
          </div>
        </div>
      )}
      <div className={classes.listText}>
        {!editable && <div className={'titleDiv'}>{renderTitle()}</div>}
        {renderSelectTitle()}
        {currentItem && <div>{getCurrentItemTitle()}</div>}
      </div>

      {_loading && <LinearProgress />}

      <List className={classes.list}>
        {showBack && (
          <ListItem size="small" className={clsx(classes.listItemRow, classes.defaultWhite, 'backIcon')} onClick={back}>
            <div className={clsx(classes.listRowIcon)}>
              <Icon>chevron_left</Icon>
            </div>
            <ListItemText>BACK</ListItemText>
          </ListItem>
        )}

        {get(currentRealm, 'children', []).map((realm, i) => {
          const isEdit = currentEdit && get(currentEdit, 'id') === get(realm, 'id');
          const isSelected = get(selected, realm.id);
          const canSelectChildren = realm.children || canShowArrow;
          const hasKey = get(realm, 'clientKey');

          if (props.hideNonUserBuilders && realm.tier === 'builder' && realm.id !== userBuilderRealm.id) {
            return null;
          }

          return (
            <ListItem
              size="small"
              className={clsx(classes.listItemRow, isEdit && classes.listItemRowEdit, isSelected && classes.isSelected)}
              key={`dropRealmsDisplay${get(realm, 'id') ?? i}`}
              onClick={canSelectChildren ? (e) => selectChildren(e, realm) : (e) => itemSelect(realm, e)}
            >
              {isEdit && renderEditRow(realm)}

              {!isEdit && (
                <>
                  {renderRowIcon(realm, i)}

                  <div className={classes.listItemRowWrapper}>
                    {editable && (
                      <ListItemText onClick={(e) => editClick(e, realm)} classes={{ root: classes.editAction }}>
                        <IconButton color="secondary" size="large">
                          <Icon>edit</Icon>
                        </IconButton>
                      </ListItemText>
                    )}

                    <Column center={'false'}>
                      <div>{getRealmName(realm)}</div>
                      {showKey && !!hasKey && <div className={'keyText'}>{`#${hasKey}`}</div>}
                    </Column>

                    {canSelectChildren && get(realm, 'tier') !== 'lot' && (
                      <ListItemSecondaryAction classes={{ root: classes.secondaryAction }}>
                        <IconButton onClick={(e) => selectChildren(e, realm)} size="large">
                          <Icon classes={{ root: classes.secondaryActionIcon }}>chevron_right</Icon>
                        </IconButton>
                      </ListItemSecondaryAction>
                    )}
                  </div>
                </>
              )}
            </ListItem>
          );
        })}

        {showAddNew && size(hist) > 0 && (
          <ListItem
            key={`${currentTierBasedOnCurrentRealm() || ''}-add`}
            size="small"
            className={clsx(classes.listItemRow, classes.defaultWhite, isNewEdit && classes.listItemRowEdit)}
            onClick={isNewEdit ? undefined : () => addNew()}
          >
            {isNewEdit && renderEditRow(currentEdit)}

            {!isNewEdit && (
              <>
                <Icon className={classes.realmRowIcon}>add_box</Icon>
                <ListItemText>{`ADD NEW ${(currentTierBasedOnCurrentRealm() || '').toUpperCase()}`}</ListItemText>
              </>
            )}
          </ListItem>
        )}
      </List>

      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />

      <ConfirmModal {...confirmModal} />

      <Modal {...modal} />
    </div>
  );
});

const DropRealms = (props) => {
  const { title, icon, showModify, alwaysShow } = props;
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState({});
  const [accessOpen, setAccessOpen] = useState(false);
  const classes = dropStyles();
  const styles = useStyles();

  const hideShow = get(props, 'hideShow', false);
  const modalTitle = get(props, 'modalTitle', 'SET ACCESS');
  const realms = get(props, 'realms', []);

  useEffect(() => {
    const map = {};
    (realms || []).map((r) => set(map, r.id, r));
    setSel(map);
  }, []);

  // Re-sync sel from realms prop when modal opens so clicking "Update"
  // without changes doesn't send an empty selection back to the parent.
  useEffect(() => {
    if (accessOpen) {
      const map = {};
      (realms || []).forEach((r) => set(map, r.id, r));
      setSel(map);
    }
  }, [accessOpen]);

  const onUpdate = () => {
    props.onUpdate && props.onUpdate(sel);
    setAccessOpen(false);
  };

  const onSelectedChange = (s) => {
    setSel(s);
  };

  const renderTitle = () => {
    if (title) {
      return title;
    }

    const obj = {};
    realms
      .filter((r) => r)
      .forEach((r) => {
        set(obj, r.tier, [...get(obj, r.tier, []), r]);
      });
    const pluralize = size(obj.community) > 1;
    const plurBuilders = size(obj.builder) > 1;
    const plurDivision = size(obj.division) > 1;
    return (
      <>
        {!!size(obj.builder) && (
          <div className={classes.titleRow}>
            {getRealmIcon({ tier: 'builder' }, classes.realmRowIcon)}
            <span style={{ marginLeft: 5 }}>{`${size(obj.builder)} ${plurBuilders ? 'Builders' : 'Builder'}`}</span>
          </div>
        )}
        {!!size(obj.division) && (
          <div className={classes.titleRow}>
            {getRealmIcon({ tier: 'division' }, classes.realmRowIcon)}
            <span style={{ marginLeft: 5 }}>{`${size(obj.division)} ${plurDivision ? 'Divisions' : 'Division'}`}</span>
          </div>
        )}
        {!!size(obj.community) && (
          <div className={classes.titleRow}>
            {getRealmIcon({ tier: 'community' }, classes.realmRowIcon)}
            <span style={{ marginLeft: 5 }}>{`${size(obj.community)} ${pluralize ? 'Communities' : 'Community'}`}</span>
          </div>
        )}
      </>
    );
  };

  const hasRealms = size(realms) > 0;

  return (
    <>
      {props.showAsFilter ? (
        <>
          <Box>
            <Row spaceBetween dontCenter>
              <FormLabel className={styles.formLabel}>Locations</FormLabel>
              <Button onClick={() => setAccessOpen(!accessOpen)} className={classes.toggleButtonRealmChoice}>
                {hasRealms ? 'MODIFY' : 'CHOOSE LOCATIONS'}
              </Button>
            </Row>
          </Box>
          <Box className={classes.fixedWidth}>
            {hasRealms && !hideShow && !alwaysShow && (
              <Box display="flex" alignItems="flex-start">
                <Box>
                  <IconButton color="error" onClick={() => props.onUpdate?.({})} size="small" className={classes.tinyMarginTop}>
                    <Icon>close</Icon>
                  </IconButton>
                </Box>
                <Box>
                  {!!icon && icon}
                  <div className={!hideShow ? classes.title : classes.firstItem}>{renderTitle()}</div>
                </Box>
                <Box>
                  <Button startIcon={<Icon>{open ? 'arrow_drop_up' : 'arrow_drop_down'}</Icon>} onClick={() => setOpen(!open)} className={clsx(classes.toggleButton, classes.tinyMarginTop)}>
                    {open ? 'HIDE' : 'SHOW'}
                  </Button>
                </Box>
              </Box>
            )}

            {size(realms) === 0 && (
              <Text italic className={classes.smallMarginTop}>
                Any
              </Text>
            )}

            <Collapse in={open || alwaysShow}>
              <div className={classes.marginLeft}>
                {realms.map((realm, i) => {
                  return (
                    <div key={`dropRealmsSelect-${realm.id ?? i}`} className={clsx(classes.titleRow, classes.title)}>
                      {getRealmIcon(realm)}
                      <div className={classes.realmText}>{get(realm, 'name', '')}</div>
                    </div>
                  );
                })}
              </div>
            </Collapse>
          </Box>
        </>
      ) : (
        <>
          <div className={classes.titleRow}>
            {!!icon && { icon }}
            <Typography className={!hideShow ? undefined : classes.firstItem}>{renderTitle()}</Typography>

            {hasRealms && !hideShow && !alwaysShow && (
              <Button startIcon={<Icon>{open ? 'arrow_drop_up' : 'arrow_drop_down'}</Icon>} onClick={() => setOpen(!open)} className={classes.toggleButton}>
                {open ? 'HIDE' : 'SHOW'}
              </Button>
            )}

            {showModify && (
              <Button onClick={() => setAccessOpen(!accessOpen)} className={classes.toggleButton}>
                {hasRealms ? 'MODIFY' : 'CHOOSE LOCATIONS'}
              </Button>
            )}
          </div>

          <Collapse in={open || alwaysShow}>
            <div className={clsx(classes.marginLeft)}>
              {realms.map((realm, i) => {
                return (
                  <div key={`dropRealmsSelect-${realm.id ?? i}`} className={clsx(classes.titleRow, classes.title)}>
                    {getRealmIcon(realm)}
                    <div className={classes.realmText}>{get(realm, 'name', '')}</div>
                  </div>
                );
              })}
            </div>
          </Collapse>
        </>
      )}

      <Modal
        open={accessOpen}
        title={modalTitle}
        content={<Access {...props} open={accessOpen} onSelectedChange={onSelectedChange} />}
        onClose={() => setAccessOpen(false)}
        buttons={[{ title: 'Update', onClick: onUpdate }]}
        classes={{
          paperScrollPaper: classes.modal,
        }}
      />
    </>
  );
};

export default DropRealms;
